import React, { useCallback, useEffect, useState } from "react";
import { getApiBaseUrl } from "../config/permanent-urls";
import {
  API_INTEGRATION_MANIFEST,
  type ApiIntegrationManifestEntry,
  type ApiIntegrationReport,
  type ApiIntegrationRowResult,
} from "../config/api-integration-manifest";

const PREFLIGHT_REQUEST_HEADERS =
  "content-type,authorization,cache-control,pragma,expires";

function buildGetHeaders(auth: boolean): HeadersInit {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  };
  if (auth) {
    const token = localStorage.getItem("token");
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
  }
  return h;
}

async function probeOptions(
  apiBase: string,
  path: string,
  origin: string,
): Promise<{ ok: boolean; status?: number; error?: string }> {
  const url = `${apiBase}${path}`;
  try {
    const response = await fetch(url, {
      method: "OPTIONS",
      headers: {
        Origin: origin,
        "Access-Control-Request-Method": "GET",
        "Access-Control-Request-Headers": PREFLIGHT_REQUEST_HEADERS,
      },
      credentials: "omit",
    });
    const allowHeaders =
      response.headers.get("access-control-allow-headers") || "";
    const allowOrigin =
      response.headers.get("access-control-allow-origin") || "";
    const ok =
      response.ok &&
      response.status === 200 &&
      allowHeaders.toLowerCase().includes("cache-control") &&
      (allowOrigin === origin || allowOrigin === "*");
    return { ok, status: response.status };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

async function probeGet(
  apiBase: string,
  path: string,
  auth: boolean,
): Promise<{ ok: boolean; status?: number; error?: string }> {
  const url = `${apiBase}${path}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: buildGetHeaders(auth),
      credentials: "omit",
      cache: "no-store",
    });
    return { ok: true, status: response.status };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

function rowPasses(
  row: ApiIntegrationRowResult,
  spec: ApiIntegrationManifestEntry,
): boolean {
  if (spec.options && !row.optionsOk) {
    return false;
  }
  if (!spec.get) {
    return true;
  }
  if (!row.getAttempted) {
    return false;
  }
  if (!row.getOk || row.getStatus === undefined) {
    return false;
  }
  return spec.getOkStatuses.includes(row.getStatus);
}

export const ApiIntegrationCheckPage: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<ApiIntegrationReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runSuite = useCallback(async () => {
    setRunning(true);
    setError(null);
    const origin = window.location.origin;
    const apiBase = getApiBaseUrl();
    const rows: ApiIntegrationRowResult[] = [];

    const startedAt = new Date().toISOString();

    for (const spec of API_INTEGRATION_MANIFEST) {
      const row: ApiIntegrationRowResult = {
        id: spec.id,
        path: spec.path,
        optionsAttempted: spec.options,
        optionsOk: false,
        getAttempted: spec.get,
        getOk: false,
      };

      if (spec.options) {
        const o = await probeOptions(apiBase, spec.path, origin);
        row.optionsOk = o.ok;
        row.optionsStatus = o.status;
        row.optionsError = o.error;
      } else {
        row.optionsOk = true;
      }

      if (spec.get) {
        const g = await probeGet(apiBase, spec.path, spec.authForGet);
        row.getStatus = g.status;
        row.getError = g.error;
        if (!g.ok) {
          row.getOk = false;
        } else if (
          g.status !== undefined &&
          spec.getOkStatuses.includes(g.status)
        ) {
          row.getOk = true;
        } else {
          row.getOk = false;
          row.getError =
            g.status !== undefined
              ? `HTTP ${g.status} not in allowed list`
              : "No status";
        }
      } else {
        row.getOk = true;
      }

      rows.push(row);
    }

    const failedCount = rows.filter(
      (r, i) => !rowPasses(r, API_INTEGRATION_MANIFEST[i]),
    ).length;

    const finishedAt = new Date().toISOString();
    const rep: ApiIntegrationReport = {
      version: 1,
      startedAt,
      finishedAt,
      finished: true,
      origin,
      apiBase,
      rows,
      corsConsoleErrors: [],
      failedCount,
    };
    setReport(rep);
    window.__KAIROS_API_INTEGRATION_RESULTS__ = rep;
    setRunning(false);
  }, []);

  useEffect(() => {
    runSuite().catch((e) => {
      setError(e instanceof Error ? e.message : String(e));
      setRunning(false);
    });
  }, [runSuite]);

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1200px" }}>
      <h1 data-testid="api-integration-title">
        API &amp; CORS integration check
      </h1>
      <p>
        Runs from the real app origin after login. Validates OPTIONS (with
        Cache-Control request headers like production) and GET where safe.
        Results are exposed on{" "}
        <code>window.__KAIROS_API_INTEGRATION_RESULTS__</code> for Playwright.
      </p>
      {running && <p data-testid="api-integration-running">Running checks…</p>}
      {error && (
        <p data-testid="api-integration-error" style={{ color: "crimson" }}>
          {error}
        </p>
      )}
      {report && (
        <>
          <p
            data-testid="api-integration-summary"
            data-pass={report.failedCount === 0 ? "true" : "false"}
          >
            Failed: {report.failedCount} / {report.rows.length}
            {report.failedCount === 0 ? " (all checks passed)" : ""}
          </p>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.9rem",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}
                >
                  Id
                </th>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}
                >
                  Path
                </th>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}
                >
                  OPTIONS
                </th>
                <th
                  style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}
                >
                  GET
                </th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: "4px 0" }}>{r.id}</td>
                  <td>
                    <code>{r.path}</code>
                  </td>
                  <td>
                    {r.optionsAttempted
                      ? r.optionsOk
                        ? `OK ${r.optionsStatus ?? ""}`
                        : `FAIL ${r.optionsError || r.optionsStatus}`
                      : "—"}
                  </td>
                  <td>
                    {r.getAttempted
                      ? r.getOk
                        ? `OK ${r.getStatus ?? ""}`
                        : `FAIL ${r.getError || r.getStatus}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};
